import React, { useEffect, useMemo } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { getBoundingBoxFromCoords } from "common/helpers/map.helpers";
import { HazardousObjectIndexed } from "../../../types/api.types";
import { AppraiserProperty } from "../../../types/appraiser.types";
import { mainPropertyBGColor } from "../../../hooks/propertyIcons.hooks";
import {
  MY_PROPERTY_SIZE_MULTIPLIER,
  mapStyle,
} from "../../../components/map/constants";
import { ComparableIconOverlayView } from "../../../components/map/ComparableIconOverlayView";
import { HazardousObjectMarker } from "./HazardousObjectMarker";

interface PropertyHazardousObjectsMapProps {
  hazardousObjects: HazardousObjectIndexed[];
  property: AppraiserProperty;
}

export const PropertyHazardousObjectsMap: React.FC<
  PropertyHazardousObjectsMapProps
> = ({ hazardousObjects, property }) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number>();

  const center = useMemo(
    () => ({ lat: property.lat, lng: property.lng }),
    [property.lat, property.lng]
  );

  const mapRef = React.useRef<google.maps.Map>();

  const setMapBounds = () => {
    // Fitting a single point would zoom the map all the way in.
    if (!mapRef.current || !hazardousObjects.length) return;

    const coords = hazardousObjects.map((hazardousObject) => ({
      lat: hazardousObject.lat,
      lng: hazardousObject.lng,
    }));
    coords.push({ lat: property.lat, lng: property.lng });

    const bounds = getBoundingBoxFromCoords(coords);
    const googleBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.minLat, bounds.minLng),
      new google.maps.LatLng(bounds.maxLat, bounds.maxLng)
    );
    mapRef.current.fitBounds(googleBounds);
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    mapRef.current = map;
    setMapBounds();
  }, []);

  useEffect(() => {
    setMapBounds();
  }, [hazardousObjects]);

  return (
    <GoogleMap
      id="property-hazardous-objects-map"
      onLoad={onLoad}
      mapContainerStyle={{
        height: "450px",
        width: "100%",
      }}
      center={center}
      zoom={12}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: "cooperative",
        clickableIcons: false,
        styles: mapStyle,
      }}
      onClick={() => setSelectedIndex(undefined)}
    >
      <ComparableIconOverlayView
        lat={property.lat}
        lng={property.lng}
        backgroundColor={mainPropertyBGColor}
        sizeMultiplier={MY_PROPERTY_SIZE_MULTIPLIER}
      />
      {hazardousObjects.map((hazardousObject) => (
        <HazardousObjectMarker
          key={hazardousObject.index}
          isSelected={selectedIndex === hazardousObject.index}
          onClick={() => setSelectedIndex(hazardousObject.index)}
          hazardousObject={hazardousObject}
          badgeCount={hazardousObject.index + 1}
        />
      ))}
    </GoogleMap>
  );
};
